import React, { useEffect, useMemo, useState } from "react";
import { AxiosResponse } from "axios";
import { useItems } from "atoms/itemAtom";
import { useAtom, useSetAtom } from "jotai";
import { isEmpty } from "lodash";
import { setModalCloseAtom } from "atoms/modalAtom/modalAtom";
import { useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useItemGroups } from "atoms/itemsGropAtom";
import { ItemsGroup } from "types";
import { useGetProductsGroup } from "features/ProductsGroup/api";
import { allItemGroups } from "constant-data/items";

import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { ModalHeader } from "components/Modal/AppModal";
import { Button, EButtonVariant } from "components/Button/Button";
import { Accordion } from "components/Accordion/Accordion";
import { AccordionParent } from "components/AccordionParent/AccordionParent";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { Option } from "components/Select/Select";

import { settingsAtom } from "../../atoms/settingsAtom";
import { TSettings } from "../../types/settings";

import { ProductPropertiesDescription } from "./ProductPropertiesDescription";
import { ProductPhotos } from "./ProductPhotos";
import styles from "./styles.module.css";
import { FormItemsValues, ProductPropertiesProps } from "./types";
import {
  useAddProduct,
  useApplyImageLinkToProduct,
  useEditProduct,
  useGetMeasureUnits,
  useGetNDS,
  useGetProductImage,
} from "./api";

const staticGroup: Option = {
  value: allItemGroups.itemgroupid,
  label: allItemGroups.name,
};

export const ProductProperties = ({
  isEdit,
}: ProductPropertiesProps): JSX.Element => {
  const queryClient = useQueryClient();
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(false);
  const [productImages, setProductImages] = useState<Array<any>>([]);
  const [newAddedImageLink, setNewAddedImageLink] = useState<Array<string>>([]);
  const [photosCollapse, setPhotosCollapse] = useState(true);
  const { itemGroupsState } = useItemGroups();
  const { itemsState } = useItems();
  const { checkedItems } = itemsState;
  const [isError, setIsError] = useState(false);
  const [settings] = useAtom(settingsAtom);
  const closeModal = useSetAtom(setModalCloseAtom);

  useGetProductImage({
    itemId: checkedItems[0]?.itemid || "",
    enabled: isEdit && !!checkedItems[0],
    onSuccess: (res) => {
      if (!isEmpty(res) && Array.isArray(res)) {
        setProductImages(res);
      }
    },
  });

  const { data: nds = [] } = useGetNDS();

  const { data: measyreUnits = [] } = useGetMeasureUnits();

  const measureOptions = measyreUnits.map((unit: any) => ({
    value: unit.measureid,
    label: unit.name,
    importcode: unit.importcode,
    deleted: unit.deleted,
  }));

  const ndsOptions = nds.map((option: any) => ({
    value: option.ndsid,
    label: option.value === "0" ? option.name : option.value,
  }));

  const { handleSubmit, control, setValue, getValues, watch } =
    useForm<FormItemsValues>({
      defaultValues: {
        itemid: isEdit ? checkedItems[0]?.itemid : "",
        name: isEdit ? checkedItems[0]?.name : "",
        code: isEdit ? checkedItems[0]?.code : "",
        itemgroupid: null,
        price: undefined,
        available: isEdit ? checkedItems[0]?.avaliable : 0,
        nodiscount: isEdit ? checkedItems[0]?.nodiscount : 0,
        service: isEdit ? checkedItems[0]?.service : 0,
        onlywhole: isEdit ? checkedItems[0]?.onlywhole : 0,
        nobonus: isEdit ? checkedItems[0]?.nobonus : 0,
        upload: isEdit ? checkedItems[0]?.upload : 0,
        position: isEdit ? checkedItems[0]?.position : undefined,
        siteimportcode: isEdit ? checkedItems[0]?.siteimportcode : "",
        importcode: isEdit ? checkedItems[0]?.importcode : "",
        measureid: null,
        ndsid: null,
      },
    });

  // const onlyWhole = watch("onlywhole");

  useEffect(() => {
    if (ndsOptions.length) {
      if (isEdit) {
        const selectedNds =
          ndsOptions.find(
            (option: any) => +option.value === checkedItems[0]?.ndsid
          ) || null;

        setValue("ndsid", selectedNds);
      } else if (settings?.ndsid) {
        const selectedNds =
          ndsOptions.find((option: any) => +option.value === settings.ndsid) ||
          null;

        setValue("ndsid", selectedNds);
      }
    }

    if (measureOptions.length) {
      if (isEdit) {
        const selectedMeasure =
          measureOptions.find(
            (option: any) => option.value === checkedItems[0]?.measureid
          ) || null;

        setValue("measureid", selectedMeasure);
      } else if (settings?.defaultmeasureid) {
        const selectedMeasure =
          measureOptions.find(
            (option: any) => option.value === settings.defaultmeasureid
          ) || null;

        setValue("measureid", selectedMeasure);
      }
    }

    if (checkedItems?.length && settings?.pricelistid && isEdit) {
      const selectedPrice =
        checkedItems[0]?.prices?.find(
          (i) => +i.pricelistid === Number(settings?.pricelistid)
        )?.price ||
        (checkedItems[0]?.prices?.length
          ? checkedItems[0]?.prices[0].price
          : undefined);

      setValue("price", selectedPrice);
    }
  }, [measureOptions, ndsOptions, settings]);

  const arr: [string, keyof FormItemsValues][] = [
    ["ID", "itemid"],
    ["*Наименование", "name"],
    ["Артикул", "code"],
    ["Номер позиции", "position"],
    ["Штрихкод", "siteimportcode"],
    ["В группе", "itemgroupid"],
    ["Ед. изм.", "measureid"],
    ["*Цена (₽)", "price"],
    ["Внешний код", "importcode"],
    ["Ставка НДС", "ndsid"],
  ];

  const firstHalf = arr.slice(0, Math.ceil(arr.length / 2));
  const secondHalf = arr.slice(Math.ceil(arr.length / 2));

  const properties = [firstHalf, secondHalf];

  const { data: items = [] } = useGetProductsGroup({ enabled: false });

  const { mutate: applyImageLink } = useApplyImageLinkToProduct({});

  const { mutate: addProduct } = useAddProduct({
    onSuccess: (res: AxiosResponse<any, any>) => {
      queryClient.invalidateQueries({ queryKey: "get-products" });

      if (!isEmpty(newAddedImageLink)) {
        for (const link in newAddedImageLink) {
          applyImageLink({
            itemid: res.data.itemid,
            link: newAddedImageLink[link],
          });
        }
      }
    },
  });

  const { mutate: editProduct } = useEditProduct({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: "get-products" });

      if (!isEmpty(newAddedImageLink)) {
        for (const link in newAddedImageLink) {
          applyImageLink({
            itemid: itemsState.checkedItems[0]!.itemid || "",
            link: newAddedImageLink[link],
          });
        }
      }
    },
  });

  const groupOptions: Option[] = useMemo(
    () => [
      staticGroup,
      ...items.map((item: ItemsGroup) => ({
        value: item?.itemgroupid || "",
        label: item?.name || "",
      })),
    ],
    [itemGroupsState]
  );

  const handleSave = async (data: FormItemsValues) => {
    if (!data.name || !data.price) {
      setIsError(true);
      return;
    }
    const productData = {
      name: data.name,
      itemgroupid: Number(data.itemgroupid?.value) || undefined,
      avaliable: data.available ? 1 : 0,
      nodiscount: data.nodiscount ? 1 : 0,
      service: data.service ? 1 : 0,
      measureid: data.measureid?.value,
      onlywhole: data.onlywhole ? 1 : 0,
      code: data.code,
      nobonus: data.nobonus ? 1 : 0,
      position: Number(data.position),
      upload: data.upload ? 1 : 0,
      ndsid: Number(data.ndsid?.value),
      importcode: data.importcode,
      price: Number(data.price),
      siteimportcode: data.siteimportcode,
      discription: "",
      deleted: 0,
    };

    if (!isEdit) {
      addProduct(productData);
    } else {
      const editProductData = {
        itemid: checkedItems[0]?.itemid,
        pricelistid: settings?.pricelistid,
        ...productData,
      };
      editProduct(editProductData);
    }

    closeModal();
  };

  useEffect(() => {
    if (isEdit && checkedItems[0]) {
      const activeItemGroupsOption =
        groupOptions.find(
          (option) => option.value === checkedItems[0].itemgroupid
        ) || null;

      setValue("itemgroupid", activeItemGroupsOption);
    } else if (itemGroupsState.activeItemsGroup) {
      setValue(
        "itemgroupid",
        groupOptions.find(
          (option) =>
            option.value === itemGroupsState.activeItemsGroup?.itemgroupid
        )!
      );
    } else {
      setValue("itemgroupid", staticGroup);
    }
  }, []);

  /*useEffect(() => {
    const freePrice = String(getValues("freeprice"));
    const isIncludesDot = freePrice.includes(".");
    const isIncludesComma = freePrice.includes(",");

    if (onlyWhole && (isIncludesDot || isIncludesComma)) {
      const indexOfPoint = freePrice.indexOf(isIncludesComma ? "," : ".");
      const newPrice = freePrice.slice(0, indexOfPoint);

      setValue("freeprice", +newPrice);
    }
  }, [onlyWhole]);*/

  return (
    <BaseLayout
      className={styles.settingsModal}
      header={
        <ModalHeader title={getValues("name") || "Создать новый продукт"} />
      }
      footer={
        <div className={styles.settingsModalFooter}>
          <Button
            onClick={handleSubmit(handleSave)}
            variant={EButtonVariant.SECONDARY}
            icon="galochka"
          >
            Сохранить
          </Button>
          {isError && <p>Небходимо заполнить "Наименование" и "Цену"</p>}
        </div>
      }
    >
      <VerticalSpace size={8} />
      <div className="mt-2">
        <Accordion
          withIcon={false}
          handleCollapse={() =>
            setDescriptionCollapsed((prevState) => !prevState)
          }
          collapsed={descriptionCollapsed}
          iconClassNames="mr-3.5"
          parent={
            <AccordionParent
              name="Описание"
              collapsed={descriptionCollapsed}
              iconClassNames="mr-3.5"
            />
          }
        >
          <ProductPropertiesDescription
            properties={properties}
            control={control}
            groupOptions={groupOptions}
            productImages={productImages}
            ndsOptions={ndsOptions}
            measureOptions={measureOptions}
            getValues={getValues}
          />
        </Accordion>
      </div>
      <div className="mt-2">
        <Accordion
          withIcon={false}
          handleCollapse={() => setPhotosCollapse((prevState) => !prevState)}
          collapsed={photosCollapse}
          iconClassNames="mr-3.5"
          parent={
            <AccordionParent
              name="Фото"
              collapsed={photosCollapse}
              iconClassNames="mr-3.5"
            />
          }
        >
          <ProductPhotos onSetNewAddedImageLink={setNewAddedImageLink} />
        </Accordion>
      </div>
    </BaseLayout>
  );
};
